import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getAuthSession, logoutAuthSession, startGoogleLogin, type AuthUser } from '../../lib/auth';
import UiIcon from '../../components/common/UiIcon';

function navClass(isActive: boolean): string {
  return isActive
    ? 'border-[#fbbf24]/70 bg-[#2a1d17] text-[#f5e5d0]'
    : 'border-[#8a6a4b]/20 bg-[#17110f] text-[#d8bfa1]/80 hover:bg-[#241913]';
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      setLoading(true);
      const session = await getAuthSession();
      if (!disposed) {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, [location.pathname]);

  const handleLogin = () => {
    startGoogleLogin('/admin');
  };

  const handleLogout = async () => {
    if (isBusy) return;
    setIsBusy(true);
    const ok = await logoutAuthSession();
    setIsBusy(false);
    if (ok) {
      setUser(null);
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#120d0b] px-6 py-16 text-[#f4e2cb]">
        <div className="mx-auto max-w-6xl border border-[#8a6a4b]/30 bg-[#17110f]/90 p-10 text-center">
          <p className="text-sm uppercase tracking-[0.24em] text-[#c9a092]/70">관리자</p>
          <h1 className="mt-4 text-3xl font-semibold">관리자 세션 확인 중</h1>
          <p className="mt-3 text-sm text-[#d8bfa1]/70">Google 로그인과 관리자 권한을 확인하고 있습니다.</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#120d0b] px-6 py-16 text-[#f4e2cb]">
        <div className="mx-auto max-w-3xl border border-[#8a6a4b]/30 bg-[#17110f]/90 p-10">
          <p className="text-sm uppercase tracking-[0.24em] text-[#c9a092]/70">관리자</p>
          <h1 className="mt-4 text-3xl font-semibold">Google 로그인이 필요합니다</h1>
          <p className="mt-3 text-sm leading-7 text-[#d8bfa1]/75">
            관리자 페이지는 기존 Google 로그인을 그대로 사용합니다. 허용된 이메일 계정으로 로그인해야 계속 진행할 수 있습니다.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleLogin}
              className="inline-flex items-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm font-medium text-[#f3dfc5] hover:bg-[#2d2019]"
            >
              <UiIcon name="arrow-right" className="h-4 w-4" />
              Google로 로그인
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-[#8a6a4b]/25 bg-[#17110f] px-4 py-3 text-sm text-[#d8bfa1]/80 hover:bg-[#1e1511]"
            >
              <UiIcon name="home" className="h-4 w-4" />
              사이트로 돌아가기
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!user.isAdmin) {
    return (
      <div className="min-h-screen bg-[#120d0b] px-6 py-16 text-[#f4e2cb]">
        <div className="mx-auto max-w-3xl border border-[#8a6a4b]/30 bg-[#17110f]/90 p-10">
          <p className="text-sm uppercase tracking-[0.24em] text-[#c9a092]/70">관리자</p>
          <h1 className="mt-4 text-3xl font-semibold">접근 권한이 없습니다</h1>
          <p className="mt-3 text-sm leading-7 text-[#d8bfa1]/75">
            이 Google 계정은 로그인되어 있지만 관리자 허용 목록에 포함되어 있지 않습니다.
          </p>
          <div className="mt-6 border border-[#8a6a4b]/25 bg-black/15 p-4 text-sm text-[#d8bfa1]/70">
            로그인 계정: {user.email || user.name || '알 수 없는 사용자'}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm font-medium text-[#f3dfc5] hover:bg-[#2d2019]"
            >
              <UiIcon name="home" className="h-4 w-4" />
              사이트로 돌아가기
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              disabled={isBusy}
              className="inline-flex items-center gap-2 border border-[#8a2f2f]/45 bg-[#311715] px-4 py-3 text-sm font-medium text-[#ffd8d8] hover:bg-[#3d1d1b] disabled:opacity-60"
            >
              <UiIcon name="close" className="h-4 w-4" />
              {isBusy ? '로그아웃 중...' : '로그아웃'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#120d0b] text-[#f4e2cb]">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(138,106,75,0.14),transparent_42%),linear-gradient(135deg,rgba(255,191,36,0.03),transparent_35%)]" />
      <div className="relative mx-auto max-w-7xl px-6 py-8">
        <header className="border border-[#8a6a4b]/25 bg-[#17110f]/90 px-5 py-5 backdrop-blur-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <Link
                to="/"
                aria-label="메인 페이지로 이동"
                className="text-xs uppercase tracking-[0.28em] text-[#c9a092]/72 transition-colors hover:text-[#f5e5d0] focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/60 focus:ring-offset-2 focus:ring-offset-[#17110f]"
              >
                Myoan Tarot
              </Link>
              <h1 className="mt-2 text-3xl font-semibold text-[#f5e5d0]">관리자 콘솔</h1>
              <p className="mt-2 text-sm text-[#d8bfa1]/72">
                리딩 상태와 결제 연동 상태를 확인하고, 실패한 생성 작업을 안전하게 다시 시도합니다.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="border border-[#7e6047]/25 bg-black/15 px-3 py-2 text-sm text-[#d8bfa1]/75">
                {user.email || user.name || 'Admin'}
              </div>
              <Link
                to="/history"
                className="inline-flex items-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-3 py-2 text-sm text-[#f3dfc5] hover:bg-[#2d2019]"
              >
                <UiIcon name="history" className="h-4 w-4" />
                리딩 히스토리
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 border border-[#8a6a4b]/25 bg-[#17110f] px-3 py-2 text-sm text-[#d8bfa1]/80 hover:bg-[#1e1511]"
              >
                <UiIcon name="home" className="h-4 w-4" />
                사이트 보기
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                disabled={isBusy}
                className="inline-flex items-center gap-2 border border-[#8a2f2f]/45 bg-[#311715] px-3 py-2 text-sm text-[#ffd8d8] hover:bg-[#3d1d1b] disabled:opacity-60"
              >
                <UiIcon name="close" className="h-4 w-4" />
                {isBusy ? '로그아웃 중...' : '로그아웃'}
              </button>
            </div>
          </div>
        </header>

        <div className="mt-6 grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="h-fit border border-[#8a6a4b]/25 bg-[#17110f]/85 p-4 backdrop-blur-sm">
            <nav className="space-y-2">
              <NavLink to="/admin" end className={({ isActive }) => `flex items-center gap-2 border px-3 py-3 text-sm transition-colors ${navClass(isActive)}`}>
                <UiIcon name="sparkles" className="h-4 w-4" />
                개요
              </NavLink>
              <NavLink to="/admin/readings" className={({ isActive }) => `flex items-center gap-2 border px-3 py-3 text-sm transition-colors ${navClass(isActive)}`}>
                <UiIcon name="book" className="h-4 w-4" />
                리딩 관리
              </NavLink>
              <NavLink to="/admin/users" className={({ isActive }) => `flex items-center gap-2 border px-3 py-3 text-sm transition-colors ${navClass(isActive)}`}>
                <UiIcon name="user" className="h-4 w-4" />
                사용자 지원
              </NavLink>
              <NavLink to="/admin/coupons" className={({ isActive }) => `flex items-center gap-2 border px-3 py-3 text-sm transition-colors ${navClass(isActive)}`}>
                <UiIcon name="coin" className="h-4 w-4" />
                쿠폰 관리
              </NavLink>
            </nav>
          </aside>

          <main className="space-y-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
